import { Box, Stack, Typography } from "@mui/material"
import { ContentCard } from "@ksana/vira-ui"
import React, { FC } from "react"
import {
  ClientFragment,
  Enum_Trend_Direction_Enum,
} from "../../generated/graphql"
import { useTrends } from "../../hooks/useTrends"
import { checkTimeForHours } from "../../utils/helpers"
import { colors } from "../../utils/theme"
import { TrendData } from "../../utils/types"
import ArrowDownIcon from "../atoms/ArrowDownIcon"
import ArrowNeutralIcon from "../atoms/ArrowNeutralIcon"
import ArrowUpIcon from "../atoms/ArrowUpIcon"
import { LiteButton } from "../atoms/button/Button"

/** Ink that follows light/dark — never hardcode denim/midnight in Loop panes. */
const inkSx = {
  color: (theme: {
    palette: { vira?: { ink?: string }; text: { primary: string } }
  }) => theme.palette.vira?.ink ?? theme.palette.text.primary,
} as const

const borderSx = {
  borderColor: (theme: {
    palette: { vira?: { border?: string }; divider: string }
  }) => theme.palette.vira?.border ?? theme.palette.divider,
} as const

interface TrendProps {
  title: string
  dataLabel: string
  data: TrendData[]
  className?: string
  variant?: "default" | "clientDetail"
}

const Trend: FC<TrendProps> = ({
  title,
  dataLabel,
  data,
  className,
  variant = "default",
}) => {
  const isClientDetail = variant === "clientDetail"
  return (
    <Box
      className={className}
      sx={{
        border: "1px solid",
        ...borderSx,
        borderRadius: isClientDetail ? "6px" : "4px",
        height: isClientDetail ? "auto" : 200,
        minHeight: isClientDetail ? 200 : undefined,
        maxWidth: isClientDetail ? "none" : 320,
        width: isClientDetail ? "100%" : undefined,
        minWidth: 0,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap={isClientDetail ? "wrap" : "nowrap"}
        gap={1}
        sx={{
          px: 1.5,
          py: isClientDetail ? 1.25 : 0,
          minHeight: 52,
          height: isClientDetail ? "auto" : 52,
          borderBottom: "1px solid",
          ...borderSx,
        }}
      >
        <Typography
          variant="body1"
          sx={{ ...inkSx, fontWeight: 700, fontSize: 14 }}
        >
          {title}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            ...inkSx,
            fontWeight: 400,
            opacity: 0.85,
            flexShrink: 0,
            fontSize: isClientDetail ? 12 : undefined,
            lineHeight: 1.3,
            whiteSpace: "nowrap",
          }}
        >
          {dataLabel}
        </Typography>
      </Stack>
      <Box sx={{ p: 1.5 }}>
        {data.map((row) => {
          const { isHour, hour, minute } = checkTimeForHours(
            row.value,
            row.unit
          )
          return (
            <Stack
              key={row.label}
              direction="row"
              justifyContent="space-between"
              alignItems="flex-start"
              gap={1}
              sx={{ mb: 1, minHeight: 24 }}
            >
              <Typography
                variant="body2"
                sx={{
                  ...inkSx,
                  fontWeight: 700,
                  minWidth: 0,
                  flex: "1 1 auto",
                  overflowWrap: "anywhere",
                  fontSize: 12,
                }}
              >
                {row.label}
              </Typography>
              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                flexShrink={0}
              >
                <Stack
                  direction="row"
                  flexWrap="nowrap"
                  sx={{ mr: 1, textAlign: "right" }}
                >
                  {!isHour ? (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          ...inkSx,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          fontSize: 12,
                        }}
                      >
                        {row.value && row.value !== "" ? row.value : "--"}
                      </Typography>
                      {row.unit ? (
                        <Typography
                          variant="body2"
                          sx={{
                            ...inkSx,
                            fontWeight: 400,
                            whiteSpace: "nowrap",
                            fontSize: 12,
                            ml: row.unit === "min" ? 0.5 : 0,
                          }}
                        >
                          {row.unit}
                        </Typography>
                      ) : null}
                    </>
                  ) : (
                    <>
                      <Typography
                        variant="body2"
                        sx={{
                          ...inkSx,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          fontSize: 12,
                        }}
                      >
                        {hour}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          ...inkSx,
                          fontWeight: 400,
                          whiteSpace: "nowrap",
                          fontSize: 12,
                          ml: 0.5,
                        }}
                      >
                        hr
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          ...inkSx,
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          fontSize: 12,
                          ml: 0.5,
                        }}
                      >
                        {minute}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          ...inkSx,
                          fontWeight: 400,
                          whiteSpace: "nowrap",
                          fontSize: 12,
                          ml: 0.5,
                        }}
                      >
                        min
                      </Typography>
                    </>
                  )}
                </Stack>
                {!row.value || row.value === "" ? (
                  <Box sx={{ height: 24, width: 24 }} />
                ) : row.direction === Enum_Trend_Direction_Enum.Up ? (
                  <ArrowUpIcon />
                ) : row.direction === Enum_Trend_Direction_Enum.Down ? (
                  <ArrowDownIcon />
                ) : row.direction === Enum_Trend_Direction_Enum.NoChange ? (
                  <ArrowNeutralIcon />
                ) : null}
              </Stack>
            </Stack>
          )
        })}
      </Box>
    </Box>
  )
}

interface Props {
  client: ClientFragment
  openTrends: () => void
  /** Client Detail Summary — ViraCard-aligned surfaces + section title outside card. */
  variant?: "default" | "clientDetail"
}

const TrendsOverview: FC<Props> = ({
  client,
  openTrends,
  variant = "default",
}) => {
  const { trends } = useTrends(client)
  const isClientDetail = variant === "clientDetail"

  const avgLabel = isClientDetail ? "Weekly avg" : "weekly average"

  const trendColumns = trends ? (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        ...(isClientDetail
          ? {
              gridAutoFlow: "row",
              gridTemplateColumns: "1fr",
              "@media (min-width:600px)": {
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
              },
              "@media (min-width:1200px)": {
                gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
              },
            }
          : { gridAutoFlow: "column" }),
      }}
    >
      <Trend
        variant={variant}
        title="Activity"
        dataLabel={avgLabel}
        data={[trends.walk, trends.exercise, trends.stationary, trends.driving]}
      />
      <Trend
        variant={variant}
        title="Sleeping"
        dataLabel={avgLabel}
        data={[
          trends.bedtime,
          trends.waketime,
          trends.timeInBed,
          trends.morningness,
        ]}
      />
      <Trend
        variant={variant}
        title="Language"
        dataLabel={avgLabel}
        data={[
          trends.positiveWords,
          trends.negativeWords,
          trends.selfFocusWords,
          trends.rigidWords,
        ]}
      />
      <Trend
        variant={variant}
        title="Other"
        dataLabel={avgLabel}
        data={[trends.activeSocialMedia, trends.timeAtHome]}
      />
    </Box>
  ) : null

  if (isClientDetail) {
    return (
      <Box sx={{ width: "100%", mb: 3 }}>
        <ContentCard title="Weekly Snapshot">{trendColumns}</ContentCard>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        border: `1px solid ${colors.neutral.clay}`,
        borderRadius: 1,
        p: 3,
        pt: 4,
        mb: 3,
        width: "100%",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3.25 }}
      >
        <Typography
          variant="h4"
          sx={{ color: colors.neutral.denim, fontWeight: 600 }}
        >
          Weekly Snapshot
        </Typography>
        <LiteButton onClick={openTrends}>
          <Typography variant="body1" sx={{ color: colors.neutral.denim }}>
            View All
          </Typography>
        </LiteButton>
      </Stack>
      {trendColumns}
    </Box>
  )
}

export default TrendsOverview
